import React, { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import CreatableSelect from 'react-select/creatable'
import { useFormikContext } from 'formik'
import makeAnimated from 'react-select/animated'
import PropTypes from 'prop-types'
import { returnSelectionFieldStyle } from '../../constants/common'
import { string } from 'yup'

const animatedComponents = makeAnimated()

const emailValidationSchema = string()
  .email('Enter a valid email address (e.g., name@example.com).')
  .matches(
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    'Enter a valid email address (e.g., name@example.com).',
  )
  .max(255)
  .required('Enter a valid email address')

const EmailMultiSelectField = ({
  label,
  name,
  placeholder = 'Enter email addresses',
  disabled = false,
  className = '',
  labelClassName = 's8 mb-2',
  maxMenuHeight = null,
  onValidationChange,
  resetKey = 0,
  onBlur: onBlurProp,
}) => {
  const {
    values,
    touched,
    errors,
    handleBlur,
    setFieldTouched,
    setFieldValue,
  } = useFormikContext()

  const [inputValue, setInputValue] = useState('')
  const [emailError, setEmailError] = useState('')

  const styles = returnSelectionFieldStyle(disabled, [])

  // Clear error and input when parent explicitly triggers a reset (e.g., Cancel button)
  useEffect(() => {
    setEmailError('')
    setInputValue('')
  }, [resetKey])

  // Inform parent (ExperienceDetails) whenever the field has or clears an error
  useEffect(() => {
    if (typeof onValidationChange === 'function') {
      onValidationChange(emailError !== '')
    }
  }, [emailError, onValidationChange])

  const validateEmail = (email) => {
    try {
      emailValidationSchema.validateSync(email)
      return true
    } catch (err) {
      return false
    }
  }

  const validateEmailWithError = async (email) => {
    try {
      await emailValidationSchema.validate(email)
      setEmailError('')
      return true
    } catch (err) {
      setEmailError(err.message)
      return false
    }
  }

  const handleInputChange = (newValue) => {
    setInputValue(newValue)
    if (newValue && newValue.trim() !== '') {
      validateEmailWithError(newValue.trim())
    } else {
      setEmailError('')
    }
  }

  const handleKeyDown = (event) => {
    if (!inputValue) return

    switch (event.key) {
      case 'Enter':
      case 'Tab':
      case ',':
        event.preventDefault()
        const email = inputValue.trim()
        if (email) {
          if (validateEmail(email)) {
            const currentValues = values[name] || []
            // Check if email already exists
            const emailExists = currentValues.some(
              (item) => item.value.toLowerCase() === email.toLowerCase(),
            )
            if (!emailExists) {
              const newOption = {
                value: email,
                label: email,
              }
              setFieldValue(name, [...currentValues, newOption])
              setInputValue('')
              setEmailError('')
            } else {
              setEmailError('This email is already added')
            }
          } else {
            validateEmailWithError(email)
          }
        }
        break
      default:
        break
    }
  }

  const handleChange = (selectedOptions) => {
    setFieldValue(name, selectedOptions || [])
    setEmailError('')
  }

  const handleCreateOption = (value) => {
    const email = value.trim()
    if (email && validateEmail(email)) {
      const currentValues = values[name] || []
      // Check if email already exists
      const emailExists = currentValues.some(
        (item) => item.value.toLowerCase() === email.toLowerCase(),
      )
      if (!emailExists) {
        const newOption = {
          value: email,
          label: email,
        }
        setFieldValue(name, [...currentValues, newOption])
        setInputValue('')
        setEmailError('')
      } else {
        setEmailError('This email is already added')
      }
    } else if (email) {
      validateEmailWithError(email)
    }
  }

  const isValidNewOption = (inputValue) => {
    const email = inputValue.trim()
    if (!email) return false
    return validateEmail(email)
  }

  return (
    <div className={`form-group mb-4 ${className}`}>
      {label && <Form.Label className={labelClassName}>{label}</Form.Label>}
      <CreatableSelect
        value={values[name] || []}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onCreateOption={handleCreateOption}
        closeMenuOnSelect={false}
        components={animatedComponents}
        isMulti
        menuIsOpen={false}
        isValid={touched.hasOwnProperty(name) && !errors.hasOwnProperty(name)}
        isInvalid={
          (touched.hasOwnProperty(name) && errors.hasOwnProperty(name)) ||
          emailError !== ''
        }
        placeholder={placeholder}
        styles={styles}
        onFocus={() => {
          setFieldTouched(name, true, false)
        }}
        onBlur={(e) => {
          handleBlur(e)
          // When the field loses focus, turn a valid pending email into a tag
          if (inputValue && inputValue.trim() !== '') {
            const email = inputValue.trim()
            if (validateEmail(email)) {
              const currentValues = values[name] || []
              const emailExists = currentValues.some(
                (item) => item.value.toLowerCase() === email.toLowerCase(),
              )
              if (!emailExists) {
                const newOption = {
                  value: email,
                  label: email,
                }
                setFieldValue(name, [...currentValues, newOption])
              }
              setInputValue('')
              setEmailError('')
            } else {
              validateEmailWithError(email)
            }
          }
          if (typeof onBlurProp === 'function') {
            onBlurProp(e)
          }
        }}
        isValidNewOption={isValidNewOption}
        isClearable
        isDisabled={disabled}
        formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
        noOptionsMessage={() => null}
        {...(maxMenuHeight && { maxMenuHeight })}
      />
      {emailError && (
        <small className='text-muted font-montserrat font-semibold font-12 form-text'>
          <span className='font-montserrat font-semibold transition d-flex justify-content-end text-error'>
            {emailError}
          </span>
        </small>
      )}
      {touched[name] && errors[name] && !emailError && (
        <small className='text-muted font-montserrat font-semibold font-12 form-text'>
          <span className='font-montserrat font-semibold transition d-flex justify-content-end text-error'>
            {errors[name]}
          </span>
        </small>
      )}
    </div>
  )
}

EmailMultiSelectField.propTypes = {
  label: PropTypes.node,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  maxMenuHeight: PropTypes.number,
}

export default EmailMultiSelectField
